/**
 * ============================================================
 * GBLOG DYNAMIC BLOCK RENDERER
 * File: assets/gblog-dynamic.js
 *
 * HOW IT WORKS
 * ─────────────────────────────────────────────────────────
 * Place a <script type="application/gblog-block"> tag anywhere
 * inside your blog post HTML content. Each tag's JSON payload
 * defines a block type + all its settings. This script finds
 * every such tag, renders the matching block component, and
 * replaces the tag in-place — so position IS the HTML position.
 *
 * USAGE IN BLOG POST HTML
 * ─────────────────────────────────────────────────────────
 *
 *   <script type="application/gblog-block">
 *   {
 *     "type": "cta_compact",
 *     "text": "🔥 Flash Sale — 50% OFF + Free Shipping",
 *     "btn_text": "Shop Now →",
 *     "btn_url": "/products/my-product",
 *     "style": "style-dark"
 *   }
 *   </script>
 *
 * SUPPORTED BLOCK TYPES + THEIR SETTINGS KEYS
 * ─────────────────────────────────────────────────────────
 *
 *  cta_banner
 *    style         "style-dark" | "style-light" | "style-gradient"
 *    eyebrow       string
 *    title         string
 *    description   string
 *    btn_text      string
 *    btn_url       string
 *    btn_style     "gblog-btn--primary" | "gblog-btn--dark" | "gblog-btn--shimmer" | "gblog-btn--light"
 *    trust_1..4    string   (trust badge labels)
 *    urgency_text  string
 *
 *  cta_compact
 *    style         "style-dark" | "style-light"
 *    text          string
 *    btn_text      string
 *    btn_url       string
 *
 *  countdown
 *    style             "style-dark" | "style-light"
 *    label             string
 *    countdown_mode    "midnight" | "session" | "fixed"
 *    session_mins      number   (default 60)
 *    fixed_date        string   ISO date e.g. "2025-12-31T23:59:00"
 *    show_days         boolean
 *    btn_text          string
 *    btn_url           string
 *    sub_text          string
 *
 *  testimonials
 *    title             string
 *    t1_text           string
 *    t1_name           string
 *    t1_stars          number (1-5)
 *    t2_text / t2_name / t2_stars
 *    t3_text / t3_name / t3_stars
 *    btn_text          string
 *    btn_url           string
 *
 *  pullquote
 *    quote             string
 *    attribution       string
 *
 *  stats_row
 *    title             string
 *    s1_num / s1_lbl
 *    s2_num / s2_lbl
 *    s3_num / s3_lbl
 *    s4_num / s4_lbl   (leave blank to hide)
 *
 *  checklist
 *    title             string
 *    i1_label / i1_sub
 *    i2_label / i2_sub
 *    i3_label / i3_sub
 *    i4_label / i4_sub  (leave blank to hide)
 *    i5_label / i5_sub  (leave blank to hide)
 *    btn_text          string
 *    btn_url           string
 *
 *  product_card
 *    tag               string   (label above title, e.g. "As Featured In This Article")
 *    name              string   (product name)
 *    description       string
 *    image_url         string   (absolute or relative URL to product image)
 *    image_alt         string
 *    price             string   (e.g. "£49.99")
 *    price_was         string   (compare-at price, leave blank to hide)
 *    discount_badge    string   (e.g. "50% OFF")
 *    btn_text          string
 *    btn_url           string
 *    btn_style         "gblog-btn--dark" | "gblog-btn--shimmer" | "gblog-btn--light"
 *
 *  image_banner
 *    image_url         string
 *    image_alt         string
 *    height            number   (px, default 280)
 *    show_overlay      boolean
 *    overlay_title     string
 *    overlay_desc      string
 *    btn_text          string
 *    btn_url           string
 *
 *  ticker
 *    msg_1 .. msg_4    string   (scroll ticker messages)
 *
 *  divider
 *    icon              string   (emoji / character, default ✦)
 *
 *  disclaimer
 *    text              string   (HTML allowed)
 *
 * ============================================================
 */

(function () {
  'use strict';

  /* ─── HELPERS ───────────────────────────────────────────── */
  function esc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function btn(text, url, cls) {
    if (!text) return '';
    cls = cls || 'gblog-btn--dark';
    return '<a href="' + esc(url || '#') + '" class="gblog-btn ' + esc(cls) + '">' + esc(text) + '</a>';
  }

  function stars(n) {
    n = parseInt(n, 10) || 5;
    n = Math.min(5, Math.max(1, n));
    return '★'.repeat(n);
  }

  /* ─── BLOCK RENDERERS ────────────────────────────────────── */

  var renderers = {

    /* ── CTA BANNER (LARGE) ─────────────────────────────── */
    cta_banner: function (s) {
      var style   = s.style || 'style-dark';
      var trusts  = [s.trust_1, s.trust_2, s.trust_3, s.trust_4].filter(Boolean);
      var trustHtml = '';
      if (trusts.length) {
        trustHtml = '<ul class="gblog-trust-list">' +
          trusts.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('') +
          '</ul>';
      }
      return '<div class="gblog-glass gblog-cta-banner ' + esc(style) + '">' +
        (s.eyebrow   ? '<div class="gblog-cta-eyebrow">' + esc(s.eyebrow) + '</div>' : '') +
        (s.title     ? '<h2 class="gblog-cta-title">'   + esc(s.title)   + '</h2>'  : '') +
        (s.description ? '<p class="gblog-cta-desc">'   + esc(s.description) + '</p>' : '') +
        btn(s.btn_text, s.btn_url, s.btn_style || 'gblog-btn--primary') +
        trustHtml +
        (s.urgency_text ? '<div class="gblog-urgency">' + esc(s.urgency_text) + '</div>' : '') +
        '</div>';
    },

    /* ── CTA COMPACT STRIP ──────────────────────────────── */
    cta_compact: function (s) {
      var style   = s.style || 'style-dark';
      var isDark  = style === 'style-dark';
      var bg      = isDark
        ? 'background:linear-gradient(135deg,rgba(0,75,83,.92),rgba(0,60,70,.95));'
        : 'background:rgba(147,202,206,.18);';
      var textCol = isDark ? '#fff' : 'var(--c-dark)';
      var btnCls  = 'gblog-btn--' + (isDark ? 'primary' : 'dark');
      return '<div class="gblog-glass" style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:14px;padding:18px 24px;' + bg + '">' +
        '<span style="font-size:14px;font-weight:700;color:' + textCol + ';flex:1;min-width:200px">' + esc(s.text || '') + '</span>' +
        (s.btn_text ? '<a href="' + esc(s.btn_url || '#') + '" class="gblog-btn ' + btnCls + '" style="padding:12px 24px;font-size:12px;flex-shrink:0">' + esc(s.btn_text) + '</a>' : '') +
        '</div>';
    },

    /* ── COUNTDOWN TIMER ────────────────────────────────── */
    countdown: function (s) {
      var style   = s.style || 'style-dark';
      var isDark  = style === 'style-dark';
      var cdId    = 'gblog-dyn-cd-' + Math.random().toString(36).slice(2, 8);
      var mode    = s.countdown_mode || 'midnight';
      var target  = mode === 'fixed' ? (s.fixed_date || 'midnight')
                  : mode === 'session' ? 'session'
                  : 'midnight';

      var bgStyle = isDark
        ? 'background:linear-gradient(135deg,rgba(0,75,83,.9),rgba(0,55,62,.95));border-color:rgba(147,202,206,.3)'
        : 'background:rgba(147,202,206,.18)';
      var labelCol  = isDark ? 'rgba(255,255,255,.9)' : 'var(--c-dark)';
      var unitLbl   = isDark ? 'rgba(255,255,255,.6)' : 'rgba(0,75,83,.6)';
      var sepCol    = isDark ? 'rgba(255,255,255,.4)' : 'rgba(0,75,83,.4)';
      var subCol    = isDark ? 'rgba(255,255,255,.55)' : 'rgba(0,75,83,.6)';

      function unit(dataAttr, lbl) {
        return '<div class="gblog-countdown-unit">' +
          '<div class="gblog-countdown-num" data-cd-' + dataAttr + '>00</div>' +
          '<div class="gblog-countdown-lbl" style="color:' + unitLbl + '">' + lbl + '</div>' +
          '</div>';
      }

      var daysHtml = s.show_days
        ? unit('days', 'Days') + '<div class="gblog-countdown-sep" style="color:' + sepCol + '">:</div>'
        : '';

      var html = '<div class="gblog-glass gblog-countdown ' + esc(style) + '" style="' + bgStyle + '">' +
        (s.label ? '<div class="gblog-countdown-label" style="color:' + labelCol + '">' + esc(s.label) + '</div>' : '') +
        '<div class="gblog-countdown-units" data-countdown="' + esc(target) + '" data-countdown-id="' + cdId + '" data-countdown-mins="' + esc(String(s.session_mins || 60)) + '">' +
          daysHtml +
          unit('hours', 'Hours') +
          '<div class="gblog-countdown-sep" style="color:' + sepCol + '">:</div>' +
          unit('mins', 'Min') +
          '<div class="gblog-countdown-sep" style="color:' + sepCol + '">:</div>' +
          unit('secs', 'Sec') +
        '</div>';

      if (s.btn_text) {
        html += '<div class="gblog-countdown-cta">' +
          '<a href="' + esc(s.btn_url || '#') + '" class="gblog-btn gblog-btn--shimmer">' + esc(s.btn_text) + '</a>' +
          (s.sub_text ? '<div class="gblog-countdown-sub" style="color:' + subCol + '">' + esc(s.sub_text) + '</div>' : '') +
          '</div>';
      }

      html += '</div>';
      return html;
    },

    /* ── TESTIMONIALS GRID ──────────────────────────────── */
    testimonials: function (s) {
      var cards = '';
      [[s.t1_text, s.t1_name, s.t1_stars],
       [s.t2_text, s.t2_name, s.t2_stars],
       [s.t3_text, s.t3_name, s.t3_stars]].forEach(function (t) {
        if (!t[0]) return;
        cards += '<div class="gblog-testi-card">' +
          '<div class="gblog-testi-stars">' + stars(t[2]) + '</div>' +
          '<div class="gblog-testi-text">' + esc(t[0]) + '</div>' +
          '<div class="gblog-testi-name">' + esc(t[1] || '') + '</div>' +
          '<div class="gblog-testi-badge">Verified Buyer</div>' +
          '</div>';
      });
      return '<div class="gblog-glass gblog-testi-grid">' +
        (s.title ? '<div class="gblog-testi-title">' + esc(s.title) + '</div>' : '') +
        '<div class="gblog-testi-cards">' + cards + '</div>' +
        (s.btn_text ? '<div style="text-align:center;margin-top:24px">' + btn(s.btn_text, s.btn_url, 'gblog-btn--dark') + '</div>' : '') +
        '</div>';
    },

    /* ── PULL QUOTE ─────────────────────────────────────── */
    pullquote: function (s) {
      return '<div class="gblog-glass gblog-pullquote">' +
        '<span class="gblog-pullquote-icon">\u201C</span>' +
        (s.quote       ? '<p class="gblog-pullquote-text">' + esc(s.quote) + '</p>' : '') +
        (s.attribution ? '<div class="gblog-pullquote-attr">' + esc(s.attribution) + '</div>' : '') +
        '</div>';
    },

    /* ── STATS ROW ──────────────────────────────────────── */
    stats_row: function (s) {
      var items = '';
      [[s.s1_num, s.s1_lbl], [s.s2_num, s.s2_lbl],
       [s.s3_num, s.s3_lbl], [s.s4_num, s.s4_lbl]].forEach(function (st) {
        if (!st[0]) return;
        items += '<div class="gblog-stat-item">' +
          '<div class="gblog-stat-num">' + esc(st[0]) + '</div>' +
          '<div class="gblog-stat-lbl">' + esc(st[1] || '') + '</div>' +
          '</div>';
      });
      return '<div class="gblog-glass gblog-stats">' +
        (s.title ? '<div class="gblog-stats-title">' + esc(s.title) + '</div>' : '') +
        '<div class="gblog-stats-row">' + items + '</div>' +
        '</div>';
    },

    /* ── FEATURE CHECKLIST ──────────────────────────────── */
    checklist: function (s) {
      var items = '';
      for (var i = 1; i <= 5; i++) {
        var lbl = s['i' + i + '_label'];
        var sub = s['i' + i + '_sub'];
        if (!lbl) continue;
        items += '<div class="gblog-check-item">' +
          '<div class="gblog-check-icon">\u2713</div>' +
          '<div>' +
            '<div class="gblog-check-label">' + esc(lbl) + '</div>' +
            (sub ? '<div class="gblog-check-sub">' + esc(sub) + '</div>' : '') +
          '</div>' +
          '</div>';
      }
      return '<div class="gblog-glass gblog-checklist">' +
        (s.title ? '<div class="gblog-checklist-title">' + esc(s.title) + '</div>' : '') +
        items +
        (s.btn_text ? '<div style="margin-top:24px;text-align:center">' + btn(s.btn_text, s.btn_url, 'gblog-btn--dark') + '</div>' : '') +
        '</div>';
    },

    /* ── PRODUCT CARD ───────────────────────────────────── */
    product_card: function (s) {
      var imgHtml = s.image_url
        ? '<img src="' + esc(s.image_url) + '" alt="' + esc(s.image_alt || s.name || '') + '" loading="lazy">'
        : '<svg class="gblog-product-img-placeholder" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<rect width="64" height="64" rx="8" fill="rgba(0,75,83,.15)"/>' +
            '<path d="M20 44l8-10 6 8 5-6 9 12H16" fill="rgba(0,75,83,.3)"/>' +
            '<circle cx="42" cy="22" r="5" fill="rgba(0,75,83,.25)"/>' +
          '</svg>';

      var priceHtml = '';
      if (s.price) {
        priceHtml = '<div class="gblog-product-price">' +
          '<span class="gblog-product-price-current">' + esc(s.price) + '</span>' +
          (s.price_was ? '<span class="gblog-product-price-was">' + esc(s.price_was) + '</span>' : '') +
          (s.discount_badge ? '<span class="gblog-product-price-badge">' + esc(s.discount_badge) + '</span>' : '') +
          '</div>';
      }

      return '<div class="gblog-glass gblog-product-card">' +
        '<div class="gblog-product-img">' + imgHtml + '</div>' +
        '<div class="gblog-product-body">' +
          (s.tag  ? '<div class="gblog-product-tag">' + esc(s.tag) + '</div>' : '') +
          '<h3 class="gblog-product-name">' + esc(s.name || 'Our Featured Product') + '</h3>' +
          (s.description ? '<p class="gblog-product-desc">' + esc(s.description) + '</p>' : '') +
          priceHtml +
          btn(s.btn_text, s.btn_url, s.btn_style || 'gblog-btn--dark') +
        '</div>' +
        '</div>';
    },

    /* ── IMAGE BANNER ───────────────────────────────────── */
    image_banner: function (s) {
      var h = s.height || 280;
      var bg = s.image_url ? '' : 'background:linear-gradient(135deg,var(--c-dark),#007a8a);';
      var imgHtml = s.image_url
        ? '<img src="' + esc(s.image_url) + '" alt="' + esc(s.image_alt || '') + '" loading="lazy">'
        : '';
      var overlayHtml = '';
      if (s.show_overlay) {
        overlayHtml = '<div class="gblog-img-banner-overlay">' +
          (s.overlay_title ? '<h3 class="gblog-cta-title">' + esc(s.overlay_title) + '</h3>' : '') +
          (s.overlay_desc  ? '<p class="gblog-cta-desc">' + esc(s.overlay_desc) + '</p>' : '') +
          btn(s.btn_text, s.btn_url, 'gblog-btn--primary') +
          '</div>';
      }
      return '<div class="gblog-img-banner" style="min-height:' + h + 'px;' + bg + '">' +
        imgHtml + overlayHtml +
        '</div>';
    },

    /* ── SCROLLING TICKER ───────────────────────────────── */
    ticker: function (s) {
      var msgs = [s.msg_1, s.msg_2, s.msg_3, s.msg_4].filter(Boolean);
      if (!msgs.length) return '';
      // Repeat 3× to ensure continuous scroll
      var inner = '';
      for (var r = 0; r < 3; r++) {
        msgs.forEach(function (m) { inner += '<span>' + esc(m) + '</span>'; });
      }
      return '<div class="gblog-ticker-wrap"><div class="gblog-ticker-inner">' + inner + '</div></div>';
    },

    /* ── DIVIDER ────────────────────────────────────────── */
    divider: function (s) {
      return '<div class="gblog-divider">' +
        '<div class="gblog-divider-line"></div>' +
        (s.icon ? '<div class="gblog-divider-icon">' + esc(s.icon) + '</div>' : '<div class="gblog-divider-icon">&#10022;</div>') +
        '<div class="gblog-divider-line"></div>' +
        '</div>';
    },

    /* ── DISCLAIMER ─────────────────────────────────────── */
    disclaimer: function (s) {
      // We allow raw HTML here to match the Liquid richtext field behaviour.
      // Only call this with trusted store-authored content.
      return '<div class="gblog-disclaimer">' + (s.text || '') + '</div>';
    }

  };

  /* ─── RENDER A SINGLE BLOCK ──────────────────────────────── */
  function renderBlock(settings) {
    var type = settings.type;
    if (!type || !renderers[type]) {
      console.warn('[gblog-dynamic] Unknown block type: ' + type);
      return null;
    }

    var inner   = renderers[type](settings);
    var wrapper = document.createElement('div');
    wrapper.className = 'gblog-block gblog-block--dynamic';
    wrapper.innerHTML = inner;
    return wrapper;
  }

  /* ─── PROCESS INLINE BLOCK TAGS ─────────────────────────── */
  function processDynamicBlocks() {
    var content = document.getElementById('gblog-content');
    if (!content) return;

    // Select all inline block definition tags inside the article
    var tags = content.querySelectorAll('script[type="application/gblog-block"]');
    tags.forEach(function (tag) {
      try {
        var settings = JSON.parse(tag.textContent || tag.innerHTML);
        var block    = renderBlock(settings);
        if (block) {
          tag.parentNode.replaceChild(block, tag);
        }
      } catch (e) {
        console.error('[gblog-dynamic] JSON parse error in inline block:', e, tag.textContent);
      }
    });
  }

  /* ─── COUNTDOWN TIMER ENGINE ─────────────────────────────── */
  // Shared with the section's own countdown system — safe to call
  // even if the section already set up Customizer countdowns.
  function setupCountdowns() {
    document.querySelectorAll('[data-countdown]').forEach(function (wrapper) {
      // Skip if already initialised by the section script
      if (wrapper.dataset.cdReady) return;
      wrapper.dataset.cdReady = '1';

      var target = wrapper.dataset.countdown;
      var end;

      if (target === 'midnight') {
        end = new Date();
        end.setHours(23, 59, 59, 0);
        if (end < new Date()) end.setDate(end.getDate() + 1);
      } else if (target === 'session') {
        var mins = parseInt(wrapper.dataset.countdownMins || '60', 10);
        var key  = 'gblog_cd_' + (wrapper.dataset.countdownId || 'default');
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, new Date(Date.now() + mins * 60000).toISOString());
        }
        end = new Date(sessionStorage.getItem(key));
      } else {
        end = new Date(target);
      }

      var dEl = wrapper.querySelector('[data-cd-days]');
      var hEl = wrapper.querySelector('[data-cd-hours]');
      var mEl = wrapper.querySelector('[data-cd-mins]');
      var sEl = wrapper.querySelector('[data-cd-secs]');

      function pad(n) { return String(n).padStart(2, '0'); }
      function tick() {
        var diff  = Math.max(0, end - new Date());
        if (dEl) dEl.textContent = pad(Math.floor(diff / 86400000));
        if (hEl) hEl.textContent = pad(Math.floor((diff % 86400000) / 3600000));
        if (mEl) mEl.textContent = pad(Math.floor((diff % 3600000)  / 60000));
        if (sEl) sEl.textContent = pad(Math.floor((diff % 60000)    / 1000));
        if (diff > 0) setTimeout(tick, 1000);
      }
      tick();
    });
  }

  /* ─── FADE-UP ANIMATIONS ─────────────────────────────────── */
  function setupAnimations() {
    var blocks = document.querySelectorAll('.gblog-block--dynamic');
    if (!blocks.length) return;

    if (!('IntersectionObserver' in window)) {
      blocks.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    blocks.forEach(function (el) { io.observe(el); });
  }

  /* ─── BOOT ───────────────────────────────────────────────── */
  function boot() {
    processDynamicBlocks();  // must run before animation setup
    setupCountdowns();
    setupAnimations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot(); // already ready (deferred script)
  }

})();
